import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Divider,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  alpha // Add this import
} from '@mui/material';
import {
  Add as AddIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
  MoreHoriz as MoreHorizIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  ReceiptLong as ReceiptIcon,
  ArrowForward as ArrowForwardIcon,
  Schedule as ScheduleIcon,
  FolderSpecial as FolderIcon,
  DonutLarge as DonutLargeIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import workflowService from '../../../services/workflowService';

// Helper function to get status color
const getStatusColor = (status) => {
  if (status.toLowerCase().includes('reject') || status.toLowerCase().includes('decline') || 
      status.toLowerCase().includes('cancel')) {
    return 'error';
  } else if (status.toLowerCase().includes('approve') || status.toLowerCase().includes('complete')) {
    return 'success';
  } else if (status.toLowerCase().includes('review') || status.toLowerCase().includes('progress')) {
    return 'warning';
  } else if (status.toLowerCase().includes('wait') || status.toLowerCase().includes('pending')) {
    return 'info';
  }
  return 'default';
};

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Dynamic Status icon based on status semantic meaning
const StatusIcon = ({ status }) => {
  // Detect common status words to infer appropriate icon
  if (status.toLowerCase().includes('reject') || status.toLowerCase().includes('decline') || 
      status.toLowerCase().includes('cancel')) {
    return <ErrorIcon fontSize="small" color="error" />;
  } else if (status.toLowerCase().includes('approve') || status.toLowerCase().includes('complete')) {
    return <CheckCircleIcon fontSize="small" color="success" />;
  } else if (status.toLowerCase().includes('review') || status.toLowerCase().includes('progress')) {
    return <MoreHorizIcon fontSize="small" color="warning" />;
  } else if (status.toLowerCase().includes('wait') || status.toLowerCase().includes('pending')) {
    return <ScheduleIcon fontSize="small" color="info" />;
  }
  return <InfoIcon fontSize="small" />;
};

const MemberWorkflowsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    inProgress: 0,
    completed: 0,
    needsAttention: 0
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchRequests();
    fetchTemplates();
  }, [selectedTab]);

  const fetchTemplates = async () => {
    try {
      const response = await workflowService.getWorkflowTemplates();
      setTemplates(response.data || []);
    } catch (err) {
      console.error('Error fetching workflow templates:', err);
      // Non-critical, so we don't set the main error
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await workflowService.getMemberRequests();
      setRequests(response.data || []);
      
      // Calculate counts for different categories
      if (response.data && response.data.length > 0) {
        const total = response.data.length;
        
        // Workflows that need member attention (pending action by member)
        const needsAttention = response.data.filter(r => {
          // Check if the current step has any actions for the member
          if (r.currentStep && r.currentStep.actions) {
            return r.currentStep.actions.some(a => 
              a.performer === 'member' || a.performer === 'both'
            );
          }
          return false;
        }).length;
        
        // Completed workflows (terminal status)
        const completed = response.data.filter(r => 
          r.isCompleted || 
          (r.currentStatus && 
           (r.currentStatus.toLowerCase().includes('complete') || 
            r.currentStatus.toLowerCase().includes('done') ||
            r.currentStatus.toLowerCase().includes('approved')))
        ).length;
        
        // In progress workflows (not completed)
        const inProgress = total - completed;
        
        setCounts({ total, inProgress, completed, needsAttention });
      }
    } catch (err) {
      console.error('Error fetching workflow requests:', err);
      setError(err.message || 'Error loading your workflow requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchRequests();
    } catch (err) {
      console.error('Error refreshing data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCreateRequest = () => {
    navigate('/member/workflows/create');
  };

  const handleViewRequest = (requestId) => {
    navigate(`/member/workflows/${requestId}`);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Format status for display
  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    
    // Convert camelCase or snake_case to Title Case With Spaces
    return status
      .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/^\w/, c => c.toUpperCase()); // Capitalize first letter
  };

  const renderSummaryCards = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          height: '100%'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: 'primary.light',
                display: 'flex'
              }}>
                <TimelineIcon color="primary" />
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1, color: 'text.secondary' }}>
                TOTAL WORKFLOWS
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {counts.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              All workflow requests
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          height: '100%',
          background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex'
              }}>
                <DonutLargeIcon />
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1, color: 'rgba(255,255,255,0.8)' }}>
                IN PROGRESS
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {counts.inProgress}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
              Active workflow requests
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          height: '100%',
          background: 'linear-gradient(135deg, #ff9966 0%, #ff5e62 100%)',
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex'
              }}>
                <WarningIcon />
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1, color: 'rgba(255,255,255,0.8)' }}>
                NEEDS ATTENTION
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {counts.needsAttention}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
              Requires your action
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          height: '100%',
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex'
              }}>
                <DoneAllIcon />
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1, color: 'rgba(255,255,255,0.8)' }}>
                COMPLETED
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {counts.completed}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
              Successfully completed
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderTemplateCards = () => (
    <Grid container spacing={3}>
      {templates.map(template => (
        <Grid item xs={12} sm={6} md={4} key={template._id}>
          <Card 
            sx={{ 
              borderRadius: 2, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                cursor: 'pointer'
              },
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
            onClick={() => navigate(`/member/workflows/create?templateId=${template._id}`)}
          >
            {!template.isActive && (
              <Chip 
                label="Draft" 
                color="warning" 
                size="small" 
                sx={{ 
                  position: 'absolute', 
                  top: 12, 
                  right: 12,
                  fontWeight: 'medium'
                }}
              />
            )}
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.light',
                  display: 'flex',
                  mr: 2
                }}>
                  <FolderIcon color="primary" />
                </Box>
                <Typography variant="h6" fontWeight="medium">
                  {template.name}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {template.description || 'No description provided'}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                Process Steps:
              </Typography>
              
              {template.steps && template.steps.length > 0 ? (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {template.steps.slice(0, 3).map((step, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.1)
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1,
                          fontSize: 12,
                          fontWeight: 'bold'
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="body2" noWrap>
                        {step.name}
                      </Typography>
                    </Box>
                  ))}
                  
                  {template.steps.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{template.steps.length - 3} more steps
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No steps defined
                </Typography>
              )}
              
              <Box sx={{ mt: 'auto', textAlign: 'right' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  size="small"
                  sx={{ borderRadius: 1 }}
                >
                  Start Request
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimelineIcon sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            Workflow Requests
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh data">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              sx={{ 
                bgcolor: 'background.paper', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateRequest}
            sx={{ 
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              px: 3,
              borderRadius: '8px'
            }}
          >
            New Request
          </Button>
        </Box>
      </Box>

      {refreshing && (
        <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab 
            label="My Workflow Requests" 
            icon={<ReceiptIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Available Workflows" 
            icon={<FolderIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {selectedTab === 0 && (
        <>
          {/* Summary Cards */}
          {!loading && !error && requests.length > 0 && renderSummaryCards()}
  
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              py: 8,
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <CircularProgress size={40} />
              <Typography color="text.secondary">
                Loading your workflow requests...
              </Typography>
            </Box>
          ) : error ? null : requests.length === 0 ? (
            <Card sx={{ 
              p: 6, 
              textAlign: 'center',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)'
            }}>
              <Box sx={{ mb: 3 }}>
                <TimelineIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7 }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'medium' }}>
                No workflow requests yet
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Create your first workflow request to start a new process.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreateRequest}
                sx={{ 
                  mt: 2, 
                  borderRadius: '8px',
                  px: 4,
                  py: 1.5,
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                  background: 'linear-gradient(90deg, #1976d2, #1565c0)'
                }}
              >
                Create Your First Request
              </Button>
            </Card>
          ) : (
            <Card sx={{ 
              borderRadius: 3, 
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
            }}>
              <CardContent sx={{ p: 0 }}>
                {isMobile ? (
                  // Mobile card view
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      {requests.map((request) => (
                        <Card 
                          key={request._id}
                          variant="outlined"
                          sx={{ 
                            p: 2, 
                            borderRadius: 2,
                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                              cursor: 'pointer'
                            }
                          }}
                          onClick={() => handleViewRequest(request._id)}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {request.title}
                            </Typography>
                            <Chip 
                              icon={<StatusIcon status={request.currentStatus} />}
                              label={formatStatus(request.currentStatus)} 
                              color={getStatusColor(request.currentStatus)}
                              size="small"
                              sx={{ fontWeight: 'medium' }}
                            />
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {request.workflowTemplate?.name || 'Unknown workflow type'}
                          </Typography>
                          
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Date
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(request.createdAt)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Current Step
                              </Typography>
                              <Typography variant="body2">
                                {typeof request.currentStep === 'string' 
                                  ? request.workflowTemplate?.steps?.find(step => step._id === request.currentStep)?.name || 'None' 
                                  : request.currentStep?.name || 'None'}
                              </Typography>
                            </Grid>
                          </Grid>
                          
                          {/* Action indicator - Show if member needs to take action */}
                          {request.currentStep && request.currentStep.actions && 
                           request.currentStep.actions.some(a => a.performer === 'member' || a.performer === 'both') && (
                            <Box sx={{ 
                              mt: 2, 
                              p: 1, 
                              borderRadius: 1, 
                              bgcolor: alpha(theme.palette.warning.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <WarningIcon color="warning" fontSize="small" />
                              <Typography variant="caption" color="warning.main" fontWeight="medium">
                                Action required
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            alignItems: 'center',
                            mt: 2
                          }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                              Updated {formatDate(request.updatedAt)}
                            </Typography>
                            <ArrowForwardIcon fontSize="small" color="primary" />
                          </Box>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  // Desktop table view
                  <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'background.paperDark' }}>
                          <TableCell>Request Date</TableCell>
                          <TableCell>Workflow Type</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Current Step</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Last Updated</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requests.map((request) => (
                          <TableRow 
                            key={request._id} 
                            hover
                            sx={{
                              transition: 'background-color 0.2s',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <TableCell>{formatDate(request.createdAt)}</TableCell>
                            <TableCell>
                              <Typography fontWeight="medium">
                                {request.workflowTemplate?.name || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>
                                {request.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>
                                {typeof request.currentStep === 'string' 
                                  ? request.workflowTemplate?.steps?.find(step => step._id === request.currentStep)?.name || 'None' 
                                  : request.currentStep?.name || 'None'}
                              </Typography>
                              {/* Action indicator if member needs to take action */}
                              {request.currentStep && request.currentStep.actions && 
                               request.currentStep.actions.some(a => a.performer === 'member' || a.performer === 'both') && (
                                <Chip
                                  size="small"
                                  label="Action needed"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ mt: 0.5, height: 20, '& .MuiChip-label': { px: 0.5, py: 0 } }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={<StatusIcon status={request.currentStatus} />}
                                label={formatStatus(request.currentStatus)} 
                                color={getStatusColor(request.currentStatus)} 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell>{formatDate(request.updatedAt)}</TableCell>
                            <TableCell align="right">
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleViewRequest(request._id)}
                                sx={{ 
                                  boxShadow: 'none',
                                  borderRadius: '8px'
                                }}
                              >
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {selectedTab === 1 && (
        <>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              py: 8,
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <CircularProgress size={40} />
              <Typography color="text.secondary">
                Loading available workflows...
              </Typography>
            </Box>
          ) : templates.length === 0 ? (
            <Card sx={{ 
              p: 6, 
              textAlign: 'center',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)'
            }}>
              <Box sx={{ mb: 3 }}>
                <FolderIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7 }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'medium' }}>
                No workflow templates available
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                There are currently no workflow templates configured for your organization.
              </Typography>
            </Card>
          ) : (
            renderTemplateCards()
          )}
        </>
      )}
    </Box>
  );
};

export default MemberWorkflowsPage;
